/**
 * The `otto-local://` transport ladder — which `{scheme, port}` a hub request is actually sent to.
 *
 * `electron/main.ts` proxies every renderer hub call to `otto-local://<host>[:port]/…`, and the
 * address the app stores says nothing about where the hub answers, so the request walks a ladder of
 * candidates:
 *
 *  - **with an explicit port** (the common case): only that port, HTTPS first on 443 (nginx), HTTP
 *    first elsewhere — trying other ports could reach a different service on the same host;
 *  - **without one**: `http:80 → http:16161 → http:443 → https:443`, which is what a stored address
 *    whose port the app no longer knows resolves to.
 *
 * The portless order is the hub's **front door first** (card `t_c2446418`, plan
 * `LOCAL-HTTP-AND-CLOUD-ADOPTION.md` §2/C1): the hub serves its own cleartext API on 80 in production
 * (`otto/src/lib/hubPorts.ts` → `PRODUCTION_HTTP_PORT`), so a plain-HTTP address must be answered by
 * the first candidate and **nothing may pay an HTTPS probe to a hub that speaks plain HTTP** — a TLS
 * ClientHello to a cleartext socket is a wasted round trip at best and, on a hub whose log only sees
 * parsed HTTP requests, is invisible while it happens. `16161` is the port that hub answered on before
 * the door moved; it stays **bound for one release** (`HUB_LEGACY_PORT`) and is tried second, so a hub
 * that upgrades before its app does is still reachable. The two `443` rungs are the legacy shape —
 * nginx terminating TLS there — and they stay last: cleartext `443` first because a box that redirects
 * `:80` to `:443` answers it (the ladder follows one redirect hop), TLS second because that is what an
 * un-upgraded reverse proxy actually serves.
 *
 * Where that explicit port is *written* depends on which transport carried the request, and both
 * shapes count:
 *
 *  - over the IPC bridge (`electron:http-request`, the app's axios layer) the URL travels as a
 *    string, so `otto-local://<host>:<port>/…` arrives with its port in the authority — read by
 *    `localProtocolExplicitPort`;
 *  - through the renderer's own `fetch` bridge the URL is parsed by **Chromium**, which drops the
 *    port of this (non-special-scheme) authority: measured in the running app,
 *    `new URL('otto-local://192.168.1.2:16162/api/hub/identity').port` is `''` and the main process
 *    receives `otto-local://192.168.1.2/api/hub/identity`. Such a call therefore carries its port in
 *    the path — `otto-local://<host>/__otto_local_port/<port>/…` (`services/hubFetch.ts` →
 *    `withHubPortMarker`) — read by `localProtocolMarkedPort`. Without this the port is silently lost
 *    and the request is answered by whichever hub on that host answered last: the class card
 *    `t_e4c55b5f` closed, and the reason a hub on a non-default port was served by the wrong box.
 *
 * Two memories order that ladder. Both are **ordering hints, never a substitute for trying**:
 *
 *  - `pathTargets` — the exact target that answered for this `host+pathname+search` (the historic
 *    cache: a device-scoped query key must never be served another device's URL);
 *  - `hostWinners` — the `{scheme, port}` that answered for this **host**, tried first for every
 *    later request to it, the same idea as `wonCandidate` in electron/discovery.ts. Without it the
 *    walk is paid once per *endpoint* instead of once per host per session — and each new endpoint
 *    pays it again for the whole life of the app.
 *
 * How a candidate fails decides what the walk costs. A port that **refuses** is free (five dead
 * candidates measured at 13 ms in total, which is why the ladder was never the slow connect on a
 * normal LAN). A candidate that **accepts and never answers** — a DROPping firewall, a VLAN, a host
 * that stopped serving 443 — holds the request until its timeout, and that is what this module
 * bounds:
 *
 *  - an **unproven** candidate gets `LOCAL_LADDER_GRACE_MS` to answer, so one silent port costs
 *    seconds instead of `LOCAL_REQUEST_TIMEOUT_MS`;
 *  - a candidate that used its whole grace is **retried once with the real budget**, so a slow but
 *    alive hub (or one behind a slow proxy) is still reached exactly as before;
 *  - a **mutating** request never gets the short budget: a re-sent POST can run twice, so nothing
 *    about the timeout may cut one short.
 *
 * Nothing here imports Electron, so the ordering rules are unit-testable
 * (src/services/localProtocolLadder.test.ts).
 */

/** The Electron-local protocol the renderer's hub calls are proxied through. */
export const LOCAL_PROTOCOL = 'otto-local';

/**
 * The path segment the renderer's fetch bridge carries a dropped port in: the
 * mirror of `HUB_PORT_MARKER` in `src/services/hubFetch.ts`.
 *
 * It exists because Chromium's URL parser drops the port of an `otto-local://`
 * authority (measured: `''` where Node says `'16162'`), while the path survives
 * untouched. The two copies of this token live in different compilation units
 * (the electron build has its own rootDir), so `localProtocolLadder.test.ts`
 * asserts that the string the bridge writes is the string this module reads.
 */
export const LOCAL_PORT_MARKER = '__otto_local_port';

/** `/<marker>/<port>` as the first path segment of a marked request. */
const PORT_MARKER_SEGMENT = new RegExp(`^/${LOCAL_PORT_MARKER}/(\\d+)(?=/|$)`);

/** How long one attempt may take before the ladder treats the candidate as not answering. */
export const LOCAL_REQUEST_TIMEOUT_MS = 30_000;

/**
 * The most a request may ask for with `LOCAL_REQUEST_BUDGET_HEADER`: a budget is
 * for work the hub does slowly *on purpose* (deleting a device purges its
 * history first), not a way to wait out a hung hub.
 */
export const MAX_LOCAL_REQUEST_BUDGET_MS = 5 * 60_000;

/**
 * Request header through which a caller widens one request's budget. The proxy
 * strips it before the request reaches the hub: it is a fact about this client's
 * patience, not part of the API.
 */
export const LOCAL_REQUEST_BUDGET_HEADER = 'x-otto-request-budget-ms';

/**
 * The budget a request asked for, clamped to `[LOCAL_REQUEST_TIMEOUT_MS, MAX_LOCAL_REQUEST_BUDGET_MS]`.
 * Anything unset, malformed, or shorter than the default keeps the default: a
 * caller can wait longer than usual, never less.
 */
export function resolveRequestBudget(requested: string | number | null | undefined): number {
  const parsed = typeof requested === 'number' ? requested : Number.parseInt(String(requested ?? ''), 10);
  if (!Number.isFinite(parsed) || parsed <= 0) return LOCAL_REQUEST_TIMEOUT_MS;
  return Math.min(Math.max(parsed, LOCAL_REQUEST_TIMEOUT_MS), MAX_LOCAL_REQUEST_BUDGET_MS);
}

/**
 * How long an *unproven* candidate may take before the walk moves on to the next one. Measured on
 * the dev hub: a healthy local hub answers in single-digit milliseconds, and a candidate that has
 * not answered in this long is either a black hole or behind one. Whatever this costs is paid once
 * per host per session (see `hostWinners`), not once per endpoint.
 */
export const LOCAL_LADDER_GRACE_MS = 2500;

/** Cap the per-path target cache so timestamped query URLs can't grow it unboundedly. */
export const LOCAL_TARGET_CACHE_MAX = 512;

/** Cap the per-host winner memory — a session talks to a handful of hubs, not to hundreds. */
export const LOCAL_WINNER_CACHE_MAX = 64;

export type LocalProtocolScheme = 'https' | 'http';

export interface LocalProtocolWinner {
  scheme: LocalProtocolScheme;
  port: number;
}

function parseLocalProtocolUrl(requestUrl: string): URL {
  const parsed = new URL(requestUrl);
  if (parsed.protocol !== `${LOCAL_PROTOCOL}:`) {
    throw new Error(`Unsupported protocol: ${parsed.protocol}`);
  }
  return parsed;
}

/**
 * The port a **marked** request names — `otto-local://host/__otto_local_port/16162/…` — or null.
 *
 * This is the shape the renderer's fetch bridge sends, because Chromium drops a port written in the
 * authority of this scheme (see the module docstring). Only the first path segment counts, and only
 * when it is the marker followed by digits, so a hub whose own path happens to start with the token
 * cannot be mistaken for a port.
 */
export function localProtocolMarkedPort(requestUrl: string): number | null {
  const parsed = parseLocalProtocolUrl(requestUrl);
  const match = PORT_MARKER_SEGMENT.exec(parsed.pathname);
  return match ? Number(match[1]) : null;
}

/**
 * The request URL with the marker removed: the address the caller named.
 *
 * Everything downstream works on this — the host winner, the per-path cache, the candidate URLs and
 * the log lines — so the marker never reaches a hub and two transports naming the same hub look the
 * same in the log. A URL without a marker is returned unchanged (same string, not a rebuilt one).
 */
export function localProtocolUnmarkedUrl(requestUrl: string): string {
  const parsed = parseLocalProtocolUrl(requestUrl);
  if (!PORT_MARKER_SEGMENT.test(parsed.pathname)) return requestUrl;
  const path = parsed.pathname.replace(PORT_MARKER_SEGMENT, '') || '/';
  return `${LOCAL_PROTOCOL}://${parsed.host}${path}${parsed.search}`;
}

/**
 * The port a request names, whichever transport wrote it: the marked path first (Chromium kept that),
 * then the authority (an IPC call, whose URL is a string and never parsed).
 */
export function localProtocolRequestPort(requestUrl: string): number | null {
  return localProtocolMarkedPort(requestUrl) ?? localProtocolExplicitPort(requestUrl);
}

/**
 * The key a winning `{scheme, port}` is remembered under: the **host**, not the endpoint. Which port
 * and scheme a host answers on does not change per path, so remembering it per path means every new
 * endpoint re-walks the ladder (and re-pays any silent candidate on it).
 */
export function localProtocolHostKey(requestUrl: string): string {
  return parseLocalProtocolUrl(requestUrl).hostname.toLowerCase();
}

/**
 * The key the resolved *target URL* is remembered under: the **address** (host and the port the
 * request names) plus its path and query.
 *
 * The pathname is included so cache entries are per-endpoint, not per-host — without it
 * `GET /api/devices` would be routed to the cached URL from `GET /api/users/me` and return the wrong
 * data. The query string is included because device-scoped endpoints carry the device in it
 * (`/api/device-automations?deviceId=X`): a pathname-only key made EVERY deviceId share one cached
 * target URL, so a request for one device was served the previously-resolved URL for ANOTHER device.
 *
 * The **port** is included for the same reason, one level up: two hubs on one host share host+path,
 * and the target that answered for one of them is a valid HTTP answer *from the other*. Measured on
 * card t_e4c55b5f: a request naming :16162 resolved (and cached `http://<host>:16162/api/hub/identity`
 * under `<host>/api/hub/identity`), and the very next request — naming :16161 — was answered by the
 * :16162 hub out of that cache. The ladder exists to honour the address the caller named; a memory
 * keyed without the port is exactly the silent-misroute class this module is here to close.
 *
 * A portless request keeps a portless key: it names no port, so it must not inherit what a ported
 * request learned.
 */
export function localProtocolPathCacheKey(requestUrl: string): string {
  // The marker is transport, not address: the same hub reached over the fetch bridge (marked path) and
  // over the IPC bridge (port in the authority) must land on ONE key, or the two transports would each
  // walk the ladder for the same endpoint.
  const parsed = parseLocalProtocolUrl(localProtocolUnmarkedUrl(requestUrl));
  const port = localProtocolRequestPort(requestUrl);
  const address = port ? `${parsed.hostname}:${port}` : parsed.host || parsed.hostname;
  return `${address}${parsed.pathname}${parsed.search}`;
}

/**
 * One candidate URL for a request: the same path, a given scheme and port. Without a port override
 * the request's own port (or none at all) is kept.
 *
 * A marked request (`otto-local://host/__otto_local_port/<port>/…`) is unmarked first, so a caller
 * that hands one in still cannot send the marker to a hub — the walk unmarks before this, and this is
 * the second lock on the same door.
 */
export function localProtocolTarget(requestUrl: string, secure = false, portOverride?: number): string {
  const parsed = parseLocalProtocolUrl(localProtocolUnmarkedUrl(requestUrl));
  const port = Number(portOverride || parsed.port || 0);
  const host = port > 0 ? `${parsed.hostname}:${port}` : parsed.hostname;
  return `${secure ? 'https' : 'http'}://${host}${parsed.pathname}${parsed.search}`;
}

/**
 * The port the stored address names explicitly, or null when the address carries none.
 *
 * Reads the **authority** only: a call that went through the renderer's `fetch` bridge has its port
 * in the marked path instead (`localProtocolMarkedPort`). Ask `localProtocolRequestPort` for "what
 * port does this request name" — that is what the walk uses.
 */
export function localProtocolExplicitPort(requestUrl: string): number | null {
  const parsed = parseLocalProtocolUrl(requestUrl);
  return parsed.port ? Number(parsed.port) : null;
}

/**
 * The candidate URLs to try, in order: the target that answered for this path, then the host's known
 * winner, then the ladder itself.
 */
export function localProtocolLadder(
  requestUrl: string,
  options: {
    cachedTargetUrl?: string | null;
    rememberedWinner?: LocalProtocolWinner | null;
    /**
     * The port this request names, when the caller already knows it — a marked path carries it, and
     * the walk passes it explicitly so the URL it builds candidates from is the unmarked one. When
     * omitted, the URL itself is asked (`localProtocolRequestPort`); pass `null` to force the portless
     * ladder for an address that names no port.
     */
    explicitPort?: number | null;
  } = {},
): string[] {
  const explicitPort = options.explicitPort !== undefined ? options.explicitPort : localProtocolRequestPort(requestUrl);
  // The candidates are built from the address, never from the transport: a marked request
  // (`otto-local://host/<marker>/<port>/…`, see the module docstring) has its port read above and the
  // marker dropped here, so no candidate URL can carry it to a hub.
  const requestTarget = localProtocolUnmarkedUrl(requestUrl);
  // Port 443 is conventionally HTTPS (nginx reverse proxy); plain HTTP to it yields a 400 "plain
  // HTTP request sent to HTTPS port" from nginx — so try HTTPS first there. Other ports default to
  // HTTP (the hub's own listener is cleartext: 80 is the front door, 16161 its legacy raw port).
  const ports = explicitPort ? [explicitPort] : [80, 16161, 443];
  const generated = explicitPort
    ? explicitPort === 443
      ? [
          localProtocolTarget(requestTarget, true, explicitPort), // HTTPS first (nginx :443)
          localProtocolTarget(requestTarget, false, explicitPort), // HTTP fallback
        ]
      : [
          localProtocolTarget(requestTarget, false, explicitPort), // HTTP first
          localProtocolTarget(requestTarget, true, explicitPort), // HTTPS fallback
        ]
    : [
        // The front door, then the legacy rungs (see the module docstring): plain HTTP first on every
        // port, and only 443 is also tried over TLS.
        ...ports.map((port) => localProtocolTarget(requestTarget, false, port)),
        localProtocolTarget(requestTarget, true, 443),
      ];

  const ordered: string[] = [];
  const push = (candidate: string | null | undefined) => {
    if (candidate && !ordered.includes(candidate)) ordered.push(candidate);
  };

  // The exact target this path resolved to last time is the most specific memory there is (it may
  // be a redirect target, e.g. nginx :80 → :443) — try it before anything else.
  push(options.cachedTargetUrl);
  // Then the host's winner, built from THIS request's path. Only the portless ladder can be
  // reordered: a request with an explicit port has its own two candidates, and a winner from
  // another port would send it somewhere the stored address never pointed at.
  if (explicitPort === null && options.rememberedWinner) {
    push(
      localProtocolTarget(requestTarget, options.rememberedWinner.scheme === 'https', options.rememberedWinner.port),
    );
  }
  generated.forEach(push);
  return ordered;
}

/** The `{scheme, port}` a resolved target URL stands for — what a host's memory is made of. */
export function localProtocolWinnerOf(resolvedUrl: string): LocalProtocolWinner | null {
  try {
    const parsed = new URL(resolvedUrl);
    const scheme = parsed.protocol === 'https:' ? 'https' : parsed.protocol === 'http:' ? 'http' : null;
    if (!scheme) return null;
    const port = Number(parsed.port) || (scheme === 'https' ? 443 : 80);
    return { scheme, port };
  } catch {
    return null;
  }
}

/** Remember a resolved target per path, evicting the oldest entry past the cap. */
export function cacheLocalProtocolTarget(pathTargets: Map<string, string>, cacheKey: string, targetUrl: string): void {
  pathTargets.set(cacheKey, targetUrl);
  if (pathTargets.size > LOCAL_TARGET_CACHE_MAX) {
    // Map preserves insertion order — evict the oldest entry.
    const oldest = pathTargets.keys().next().value as string | undefined;
    if (oldest) pathTargets.delete(oldest);
  }
}

/** Remember the `{scheme, port}` that answered for a host, evicting the oldest entry past the cap. */
export function rememberLocalProtocolWinner(
  winners: Map<string, LocalProtocolWinner>,
  hostKey: string,
  winner: LocalProtocolWinner,
): void {
  winners.delete(hostKey);
  winners.set(hostKey, winner);
  if (winners.size > LOCAL_WINNER_CACHE_MAX) {
    const oldest = winners.keys().next().value as string | undefined;
    if (oldest) winners.delete(oldest);
  }
}

export interface LocalProtocolWalkDeps {
  /**
   * Perform one attempt against a resolved target URL. `timeoutMs` is the whole budget for that
   * attempt — the caller's transport must honour it even when the candidate is silent (electron/
   * main.ts bounds both the first byte and the socket's idle time with it).
   */
  fetchTarget(targetUrl: string, init: RequestInit, timeoutMs: number): Promise<Response>;
  /** Where the ladder's own lines go (main.ts: `console.error`). */
  log(line: string): void;
  /** `+Nms` elapsed-since-process-start stamp, as the rest of the main-process log uses. */
  stamp(): string;
  /** Per-path memory of the target that resolved (mutated in place). */
  pathTargets: Map<string, string>;
  /** Per-host memory of the `{scheme, port}` that answered (mutated in place). */
  hostWinners: Map<string, LocalProtocolWinner>;
  /**
   * Overridden in tests only: the clock the grace decision reads.
   *
   * `usedGrace` — "this candidate accepted and said nothing", which is what earns a candidate its
   * retry with the full budget — is a comparison of *elapsed* time against the grace, so a test that
   * wants to decide it must be able to move the clock rather than wait for one (card `t_02e5d706`:
   * the cases that measured that with a stopwatch were asserting the machine, not the walk).
   */
  now?: () => number;
  /** Overridden in tests only. */
  graceMs?: number;
  /** Overridden in tests only. */
  fullTimeoutMs?: number;
}

type LadderAttempt =
  | { kind: 'answered'; response: Response; resolvedUrl: string; apiError: boolean }
  | { kind: 'wrong-scheme'; response: Response; resolvedUrl: string }
  | { kind: 'failed'; error: unknown; usedGrace: boolean };

/**
 * Resolve and perform one hub request through the ladder. Returns the response plus the URL that
 * answered, or throws with the last candidate's error when nothing answered.
 */
export async function walkLocalProtocolLadder(
  requestUrl: string,
  init: RequestInit,
  deps: LocalProtocolWalkDeps,
): Promise<{ response: Response; resolvedUrl: string }> {
  // Two shapes of the same request: `explicitPort` is the port the caller names (the marked path when
  // Chromium carried the call, the authority when the IPC bridge did), and `requestTarget` is the URL
  // without the marker — the address the caller meant, which is what the caches, the candidates and
  // the log lines use. Every helper below validates the scheme.
  const explicitPort = localProtocolRequestPort(requestUrl);
  const requestTarget = localProtocolUnmarkedUrl(requestUrl);
  const hostKey = localProtocolHostKey(requestTarget);
  // …while the per-path memory is keyed on the ORIGINAL request, because the port it names is part of
  // the address: two hubs on one host share `host+path` and one of them must not answer for the other.
  const cacheKey = localProtocolPathCacheKey(requestUrl);
  const fullTimeoutMs = deps.fullTimeoutMs ?? LOCAL_REQUEST_TIMEOUT_MS;
  const now = deps.now ?? Date.now;
  const method = (init.method || 'GET').toUpperCase();
  // A re-sent POST can run twice, so only idempotent requests may be cut short. For anything else
  // the grace IS the full budget, which is exactly the behaviour that predates this module.
  const safeMethod = method === 'GET' || method === 'HEAD';
  const graceMs = safeMethod ? Math.min(deps.graceMs ?? LOCAL_LADDER_GRACE_MS, fullTimeoutMs) : fullTimeoutMs;

  const rememberedWinner = explicitPort === null ? (deps.hostWinners.get(hostKey) ?? null) : null;
  const winnerUrl = rememberedWinner
    ? localProtocolTarget(requestTarget, rememberedWinner.scheme === 'https', rememberedWinner.port)
    : null;
  const candidates = localProtocolLadder(requestTarget, {
    cachedTargetUrl: deps.pathTargets.get(cacheKey) ?? null,
    rememberedWinner,
    explicitPort,
  });

  const ladderStartedAt = now();
  let attempted = 0;
  let lastError: unknown = null;
  let lastHttpErrorResponse: { response: Response; resolvedUrl: string } | null = null;
  const slowCandidates: string[] = [];

  /** One attempt, including the single permanent-redirect hop, with the ladder's own logging. */
  const attempt = async (targetUrl: string, timeoutMs: number, total: number): Promise<LadderAttempt> => {
    const attemptStartedAt = now();
    attempted += 1;
    const nth = attempted;
    try {
      let response = await deps.fetchTarget(targetUrl, init, timeoutMs);
      let resolvedUrl = targetUrl;

      // Follow one permanent redirect hop — nginx bounces plain-HTTP port-80 traffic to HTTPS with a
      // 301. Without this, a hub address stored with a redirect-only port (e.g. :80) makes every
      // request come back as an HTML 301 and POSTs fail with cryptic client errors (register/login).
      if (response.status === 301 || response.status === 308) {
        const location = response.headers.get('location');
        if (location) {
          try {
            const redirectTarget = new URL(location, targetUrl).toString();
            if (redirectTarget !== targetUrl) {
              response = await deps.fetchTarget(redirectTarget, init, fullTimeoutMs);
              resolvedUrl = redirectTarget;
            }
          } catch {
            // Malformed Location — fall through with the redirect response.
          }
        }
      }

      // Only a 400 with an HTML/text body is the "plain HTTP request sent to HTTPS port" page from
      // nginx — i.e. the wrong scheme for this port. Skip it and try the next candidate so the real
      // (HTTPS) endpoint is reached. Real API errors (401/403/404/500 … with a JSON body) must be
      // returned as-is — they came from the actual hub.
      if (response.status >= 400) {
        const contentType = (response.headers.get('content-type') || '').toLowerCase();
        const isPlainHttp400 =
          response.status === 400 && (contentType.includes('text/html') || contentType.includes('text/plain'));
        if (isPlainHttp400) {
          deps.log(
            `[ElectronHTTP]   otto-local candidate ${targetUrl} → ${response.status} (html), trying next [${deps.stamp()}, after ${now() - attemptStartedAt}ms]`,
          );
          return { kind: 'wrong-scheme', response, resolvedUrl };
        }
      }

      return { kind: 'answered', response, resolvedUrl, apiError: response.status >= 400 };
    } catch (error) {
      const elapsed = now() - attemptStartedAt;
      // A candidate that used its whole grace did not refuse — it accepted and said nothing. Only
      // such a candidate gets a second chance with the real budget.
      const usedGrace = graceMs < fullTimeoutMs && elapsed >= graceMs * 0.9;
      const failure = error instanceof Error ? error.message : String(error);
      deps.log(
        `[ElectronHTTP]   otto-local candidate failed: ${targetUrl} — ${failure} [${deps.stamp()}, candidate ${nth}/${total} gave up after ${elapsed}ms]`,
      );
      return { kind: 'failed', error, usedGrace };
    }
  };

  /** An answered candidate settles the request: remember it, then report it. */
  const settle = (answer: Extract<LadderAttempt, { kind: 'answered' }>, total: number) => {
    cacheLocalProtocolTarget(deps.pathTargets, cacheKey, answer.resolvedUrl);
    if (explicitPort === null) {
      const winner = localProtocolWinnerOf(answer.resolvedUrl);
      if (winner) rememberLocalProtocolWinner(deps.hostWinners, hostKey, winner);
    }
    const verdict = answer.apiError ? 'otto-local resolved (API error):' : 'otto-local resolved:';
    deps.log(
      `[ElectronHTTP]   ${verdict} ${requestTarget} → ${answer.resolvedUrl} (${answer.response.status}) [${deps.stamp()}, candidate ${attempted}/${total}, ladder ${now() - ladderStartedAt}ms]`,
    );
    return { response: answer.response, resolvedUrl: answer.resolvedUrl };
  };

  for (const targetUrl of candidates) {
    const timeoutMs = targetUrl === winnerUrl ? fullTimeoutMs : graceMs;
    const result = await attempt(targetUrl, timeoutMs, candidates.length);
    if (result.kind === 'answered') return settle(result, candidates.length);
    if (result.kind === 'wrong-scheme') {
      lastHttpErrorResponse = { response: result.response, resolvedUrl: result.resolvedUrl };
      continue;
    }
    lastError = result.error;
    if (result.usedGrace && !slowCandidates.includes(targetUrl)) slowCandidates.push(targetUrl);
  }

  // Nothing answered inside the grace. A candidate that stayed silent may still be the hub behind a
  // slow link, so give those the full budget before failing the request.
  const retryTotal = candidates.length + slowCandidates.length;
  for (const targetUrl of slowCandidates) {
    const result = await attempt(targetUrl, fullTimeoutMs, retryTotal);
    if (result.kind === 'answered') return settle(result, retryTotal);
    if (result.kind === 'wrong-scheme') {
      lastHttpErrorResponse = { response: result.response, resolvedUrl: result.resolvedUrl };
      continue;
    }
    lastError = result.error;
  }

  // Every candidate failed at the network level — but if one did return a real HTTP error (e.g.
  // 401/404 from the actual API), surface that instead.
  if (lastHttpErrorResponse) {
    deps.log(
      `[ElectronHTTP]   otto-local no successful candidate for ${requestTarget}; returning ${lastHttpErrorResponse.resolvedUrl} (${lastHttpErrorResponse.response.status})`,
    );
    return lastHttpErrorResponse;
  }

  deps.pathTargets.delete(cacheKey);
  // Nothing answered: drop the remembered winner so the next walk tries the whole ladder again
  // instead of trusting a stale one (the same rule as `probeHub` in electron/discovery.ts).
  if (explicitPort === null) deps.hostWinners.delete(hostKey);
  deps.log(
    `[ElectronHTTP]   otto-local ALL candidates failed for ${requestTarget} [${deps.stamp()}, ${attempted} candidates walked in ${now() - ladderStartedAt}ms]`,
  );

  throw lastError || new Error('Local hub request failed');
}
